import {v4 as uuidV4} from 'uuid';

import {ONE_SECOND_MS} from '@/shared/consts/time';
import {delay} from '@/shared/lib/delay';
import {getCampaign} from '@/shared/lib/localStorage/campaign';
import {
  getConfig,
  getExternalUuid,
  setConfig,
  setExternalUuid,
} from '@/shared/lib/localStorage/internalAnalytics';
import {notNullValues} from '@/shared/lib/object';

import {init as initRequest} from './api/init';
import {sendEvents as sendEventsRequest} from './api/sendEvents';
import {SUPPORT_EVENTS} from './consts';
import {supportEvent} from './lib/supportEvent';
import {
  Options,
  SharedState,
  State,
  SupportEvents,
  TrackInternalArgs,
} from './types';

const NOT_INITIALIZED_EVENT_SIZE = 100;
const TRY_INIT_INTERVAL = 3 * ONE_SECOND_MS;
const SEND_INTERVAL = 3 * ONE_SECOND_MS;
const TRY_INIT_ATTEMPTS = 3;
const SEND_EVENTS_MAX_BULK_SIZE = 10;

const defaultState: State = {
  initStatus: 'not_initialized',
  initPromise: null,
  sendInterval: SEND_INTERVAL,
  initializeAttempts: 0,
  scheduleTimeoutId: undefined,
  events: [],
  options: {
    enable: true,
    sendInterval: SEND_INTERVAL,
    clearEvents: false,
    path: '/',
  },
  abtests: [],
};

let state: State = defaultState;

function makeState(options?: Options): State {
  const stateOptions = {
    ...defaultState.options,
    ...options,
  };

  return {
    ...state,
    events: stateOptions?.clearEvents ? [] : defaultState.events,
    sendInterval: stateOptions.sendInterval,
    options: stateOptions,
  };
}

export async function init(options: Options): Promise<SharedState> {
  state = makeState(options);

  if (state.initStatus === 'initialized' || !state.options.enable) {
    return {
      abtests: state.abtests,
    };
  }

  if (!state.initPromise) {
    state.initPromise = runInitAnalytics({
      path: state.options.path,
    });
  }

  return state.initPromise;
}

export function trackInternal<Name extends SupportEvents>(
  ...args: TrackInternalArgs<Name>
) {
  const [name, {ctx_id} = {}] = args as [SupportEvents, {ctx_id?: string}?];

  if (!supportEvent(name) || !state.options.enable) {
    return;
  }

  const context: Record<string, number> | undefined =
    SUPPORT_EVENTS[name].context;

  state.events.push({
    n: name,
    ts: Date.now(),
    ctx_id: ctx_id === undefined ? undefined : context?.[ctx_id],
  });

  const notInitializedOverSizeEvents =
    state.events.length - NOT_INITIALIZED_EVENT_SIZE;
  if (state.initStatus !== 'initialized' && notInitializedOverSizeEvents > 0) {
    state.events.splice(0, notInitializedOverSizeEvents);
  }

  scheduleSendEvents();
}

function scheduleSendEvents() {
  if (state.scheduleTimeoutId) {
    return;
  }

  state.scheduleTimeoutId = setTimeout(async () => {
    try {
      await sendEvents();

      if (state.events.length > 0) {
        scheduleSendEvents();
      }
    } catch {
      // noop
    } finally {
      state.scheduleTimeoutId = undefined;
    }
  }, state.sendInterval);
}

async function sendEvents() {
  if (!state.events.length || state.initStatus !== 'initialized') {
    return;
  }

  const pushEvents = state.events.splice(0, SEND_EVENTS_MAX_BULK_SIZE);
  try {
    await sendEventsRequest(pushEvents, {
      uuid: state.uuid,
    });
  } catch (error) {
    state.events.unshift(...pushEvents);
    throw error;
  }
}

async function runInitAnalytics({path}: {path: string}): Promise<SharedState> {
  if (state.initializeAttempts >= TRY_INIT_ATTEMPTS) {
    state.initStatus = 'error';
    throw new Error('Reached init attempts');
  }

  state.initStatus = 'initializing';

  const storedConfig = getConfig();
  if (storedConfig) {
    state.uuid = storedConfig.uuid;
    state.abtests = storedConfig.abtests ?? [];
    state.initStatus = 'initialized';
    return {
      abtests: state.abtests,
    };
  }

  const campaign = getCampaign();
  let externalUuid = getExternalUuid();

  if (!externalUuid) {
    externalUuid = uuidV4();
    setExternalUuid(externalUuid);
  }

  const cypressEnv = typeof window !== 'undefined' && Boolean(window.Cypress);

  if (cypressEnv) {
    state.uuid = 'cypress-env';
    state.abtests = [];
    state.initStatus = 'initialized';
    return {
      abtests: state.abtests,
    };
  }

  try {
    const {uuid, abtests} = await initRequest(
      notNullValues({
        path,
        uuid: externalUuid,
        campaign_info: campaign?.value,
      })
    );

    state.uuid = uuid;
    state.abtests = abtests;
    state.initStatus = 'initialized';
    setConfig({
      uuid,
      abtests,
    });
    scheduleSendEvents();
  } catch {
    state.initializeAttempts++;
    await delay(TRY_INIT_INTERVAL);
    return runInitAnalytics({path});
  }

  return {
    abtests: state.abtests,
  };
}
